/**
 * useOosFilters Hook
 *
 * Manages filter state for the Issues page OOS tab with debounced search
 * inputs. Filtering / grouping / sorting itself happens server-side via
 * `useOosIssueGroups` (BLU-448) — this hook only owns the UI state.
 */
import { useState, useCallback } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { OosFilterState } from '../types';
import { DEFAULT_OOS_FILTERS } from '../types';

/**
 * Hook for managing OOS filter state with debounced search.
 *
 * `initialSearch` seeds the starting state only. The debounced value starts
 * from it too, so the first query is already filtered; Reset still returns to
 * the defaults.
 */
export function useOosFilters({ initialSearch }: { initialSearch?: string } = {}) {
  const [filters, setFilters] = useState<OosFilterState>(() =>
    initialSearch ? { ...DEFAULT_OOS_FILTERS, search: initialSearch } : DEFAULT_OOS_FILTERS
  );

  const updateFilter = useCallback(
    <K extends keyof OosFilterState>(key: K, value: OosFilterState[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_OOS_FILTERS);
  }, []);

  // Debounce search inputs (300ms) so we don't fire a backend query per keystroke
  const debouncedSearch = useDebouncedValue(filters.search, 300);
  const debouncedSkuSearch = useDebouncedValue(filters.skuSearch, 300);

  return {
    // Filter state with debounced search values — pass this to the server query
    filters: {
      ...filters,
      search: debouncedSearch,
      skuSearch: debouncedSkuSearch,
    },
    // Raw state for input display — shows what the user typed immediately
    rawFilters: filters,
    updateFilter,
    resetFilters,
  };
}
